// [#d161bf7d] Componente reutilizável de range de prazo:
// data início + prazo (dias) → data conclusão calculada automaticamente.
// Usado em propostas, OS e contratos.
import React, { useEffect, useRef } from 'react';
import { fmtDataBr, hojeIso } from '../utils/dataFmt';

export interface RangePrazoValue {
  inicio: string | null;       // ISO yyyy-mm-dd
  prazo_dias: number | null;
  conclusao: string | null;    // ISO yyyy-mm-dd (calculado)
}

interface Props {
  value: RangePrazoValue;
  onChange: (v: RangePrazoValue) => void;
  disabled?: boolean;
  // Se passado, usa pra parsear "30 dias corridos" → 30 ao popular pela primeira vez
  prazoTextoSugerido?: string | null;
  labelInicio?: string;
  labelPrazo?: string;
  labelConclusao?: string;
}

/** Parse de string como "30 dias corridos", "45", "60d" → número de dias.
 *  Retorna null se não for parseável. */
export function parsePrazoTexto(s: string | null | undefined): number | null {
  if (!s) return null;
  const t = String(s).trim().toLowerCase();
  // primeiro número que aparece
  const m = t.match(/(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** data ISO + N dias → data ISO. Se data inválida ou n null, retorna null. */
export function somarDias(dataIso: string | null, n: number | null): string | null {
  if (!dataIso || n == null || !Number.isFinite(n)) return null;
  const d = new Date(dataIso + 'T12:00:00'); // meio-dia evita problema de timezone
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

// Re-export do util compartilhado pra manter compatibilidade com importações
// existentes. Implementação real em ../utils/dataFmt.
export { fmtDataBr } from '../utils/dataFmt';

export default function RangePrazo({
  value, onChange, disabled,
  prazoTextoSugerido,
  labelInicio = 'Data início',
  labelPrazo = 'Prazo (dias)',
  labelConclusao = 'Conclusão prevista',
}: Props) {
  // Preset apenas na 1ª renderização: data início = hoje + parse do prazo sugerido.
  // Usa ref pra rodar exatamente uma vez (não dispara em re-renders subsequentes).
  const presetAplicado = useRef(false);
  useEffect(() => {
    if (presetAplicado.current) return;
    if (value.inicio && value.prazo_dias != null) {
      // já tem valores estruturados — pula preset
      presetAplicado.current = true;
      return;
    }
    const inicio = value.inicio || hojeIso();
    const prazo = value.prazo_dias != null ? value.prazo_dias : parsePrazoTexto(prazoTextoSugerido);
    if (prazo == null && !value.inicio) {
      // sem prazo sugerido nem início — só popula início com hoje
      presetAplicado.current = true;
      onChange({
        inicio,
        prazo_dias: value.prazo_dias,
        conclusao: somarDias(inicio, value.prazo_dias),
      });
      return;
    }
    if (prazo != null) {
      presetAplicado.current = true;
      onChange({
        inicio,
        prazo_dias: prazo,
        conclusao: somarDias(inicio, prazo),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prazoTextoSugerido]);

  function setInicio(novoIni: string) {
    onChange({
      inicio: novoIni || null,
      prazo_dias: value.prazo_dias,
      conclusao: somarDias(novoIni, value.prazo_dias),
    });
  }
  function setPrazo(novoPrazo: string) {
    const n = novoPrazo === '' ? null : parseInt(novoPrazo, 10);
    onChange({
      inicio: value.inicio,
      prazo_dias: n,
      conclusao: somarDias(value.inicio, n),
    });
  }

  const hoje = hojeIso();
  const inicioPassado = value.inicio && value.inicio < hoje;
  const prazoZero = value.prazo_dias != null && value.prazo_dias <= 0;
  const conclusaoExibida = value.conclusao || somarDias(value.inicio, value.prazo_dias);

  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 5,
    border: '0.5px solid #ddd', boxSizing: 'border-box', height: 32,
  };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 24px 1.2fr', gap: 8, alignItems: 'flex-start' }}>
      <div>
        <label style={lbl}>{labelInicio}</label>
        <input type="date" value={value.inicio || ''} disabled={disabled}
          onChange={e => setInicio(e.target.value)} style={inp} />
        {inicioPassado && (
          <div style={{ fontSize: 10, color: '#B07500', marginTop: 2 }}>⚠ data anterior a hoje</div>
        )}
      </div>
      <div>
        <label style={lbl}>{labelPrazo}</label>
        <input type="number" min={1} value={value.prazo_dias ?? ''} disabled={disabled}
          onChange={e => setPrazo(e.target.value)}
          placeholder={prazoTextoSugerido ? `ex: ${parsePrazoTexto(prazoTextoSugerido) || 30}` : 'ex: 30'}
          style={{ ...inp, ...(prazoZero ? { borderColor: '#B91626' } : {}) }} />
        {prazoZero && <div style={{ fontSize: 10, color: '#B91626', marginTop: 2 }}>deve ser &gt; 0</div>}
      </div>
      {/* Seta alinhada com os inputs (não com os labels) */}
      <div style={{
        textAlign: 'center', fontSize: 18, color: '#888',
        height: 32, lineHeight: '32px', marginTop: 19,
      }}>→</div>
      <div>
        <label style={lbl}>{labelConclusao}</label>
        <div style={{
          ...inp, background: '#FAFBFF', color: '#020D7F', fontWeight: 600,
          border: '0.5px solid #CDD4EA', cursor: 'default',
          display: 'flex', alignItems: 'center',
        }}>
          {fmtDataBr(conclusaoExibida)}
        </div>
        {conclusaoExibida && (
          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
            calculado: início + prazo
          </div>
        )}
      </div>
    </div>
  );
}
